import { useForm, Controller } from "react-hook-form";
import React, { useContext, useState } from "react";
import { Alert, Box, Card, TextField, Typography, useTheme } from "@mui/material";
import { colors } from "styles/colors";
import { BreakpointContext } from "contexts/breakpoint";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useHistory, useLocation } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { CapableLogoLong } from "components/CapableIcons";
import {
  emailRequiredMessage,
  emailValidationErrorMessage,
  passwordRegEx,
  passwordRequirementsMessage,
} from "./Auth.utils";
import { InputAdornmentForPassword } from "./InputAdornmentForPassword";
import { useResetPassword } from "./useResetPassword";

const validationSchema = z.object({
  email: z.string().min(1, { message: emailRequiredMessage }).email({
    message: emailValidationErrorMessage,
  }),
  code: z.string().min(1, { message: "Please enter your code" }),
  password: z.string().regex(passwordRegEx, {
    message: passwordRequirementsMessage,
  }),
});

type ValidationSchema = z.infer<typeof validationSchema>;

export const ResetPassword = () => {
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const [showPassword, setShowPassword] = useState(false);

  const { isMobileView } = useContext(BreakpointContext);
  const theme = useTheme();

  const history = useHistory();

  const emailFromParams = urlSearchParams.get("email");
  const encodedEmail = emailFromParams ? encodeURIComponent(emailFromParams) : undefined;

  const resendCodeLink = encodedEmail
    ? `/forgot_password?email=${encodedEmail}`
    : `/forgot_password`;

  const { resetPassword, isError, isLoading } = useResetPassword(() => {
    history.push(`/reset_password_confirmation`);
  });
  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors, isValid },
  } = useForm<ValidationSchema>({
    defaultValues: {
      email: urlSearchParams.get("email") ?? "",
    },
    resolver: zodResolver(validationSchema),
  });

  const processForm = async (data: ValidationSchema) => {
    await resetPassword(data.email, data.password, data.code);
  };

  const formControlStyles = {
    margin: theme.spacing(1, 0),
    width: "100%",
  };

  return (
    <Box
      sx={{
        backgroundColor: colors.lightGrey1,
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      <Card
        sx={{
          width: isMobileView ? "100%" : "500px",
          marginTop: "100px",
          padding: theme.spacing(5),
          display: "flex",
          flexDirection: "column",
          alignItems: "left",
        }}
      >
        <Box sx={{ display: "flex", alignContent: "center", justifyContent: "center" }}>
          <CapableLogoLong />
        </Box>

        {isError && (
          <Alert sx={{ marginTop: 2 }} severity="error">
            {isError}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h3"
            sx={{ marginBottom: theme.spacing(2), marginTop: theme.spacing(4) }}
          >
            Reset your password
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: theme.spacing(2) }}>
            Enter the verification code that was sent to your email
          </Typography>
        </Box>
        <form onSubmit={handleSubmit(processForm)}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <>
                <TextField
                  label="Email"
                  variant="outlined"
                  style={formControlStyles}
                  {...field}
                  onBlur={() => {
                    trigger("email");
                  }}
                  onChange={(e) => {
                    setTimeout(() => {
                      trigger("email");
                    }, 5000);
                    field.onChange(e);
                  }}
                  helperText={errors.email?.message}
                  error={!!errors.email}
                />
              </>
            )}
          />

          <Controller
            name="code"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <>
                <TextField
                  label="Code"
                  variant="outlined"
                  style={formControlStyles}
                  {...field}
                  onBlur={() => {
                    trigger("code");
                  }}
                  helperText={errors.code?.message}
                  error={!!errors.code}
                />
              </>
            )}
          />

          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <>
                <TextField
                  label="New Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  style={formControlStyles}
                  {...field}
                  onBlur={() => {
                    trigger("password");
                  }}
                  onChange={(e) => {
                    setTimeout(() => {
                      trigger("password");
                    }, 5000);
                    e.target.value = e.target.value.trim();
                    field.onChange(e);
                  }}
                  helperText={errors.password?.message}
                  error={!!errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornmentForPassword
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                        title={passwordRequirementsMessage}
                        ariaLabel="Toggle password visibility"
                      />
                    ),
                  }}
                />
              </>
            )}
          />
          <LoadingButton
            loading={isLoading}
            variant="contained"
            disabled={!isValid}
            onClick={handleSubmit(processForm)}
            style={formControlStyles}
            sx={{ marginTop: (theme) => theme.spacing(3) }}
          >
            Submit
          </LoadingButton>
        </form>
        <Box display={"flex"} justifyContent="space-between">
          <Link to="/">Back to sign in</Link>
          <Link to={resendCodeLink}>Resend code</Link>
        </Box>
      </Card>
    </Box>
  );
};
